import { Anchor, Button, Divider, Flex, Grid, Menu, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "src/components/shared/BackButton/BackButton";
import { getOrigin } from "src/environment";
import { useIsOnline } from "src/hooks";
import { useSelector } from "src/redux/hooks";

import SetLang from "./SetLang/SetLang";

interface ICollectionBar {
  links: ILink[];
  groupName: string;
}

interface ILink {
  link: string;
  linkName: string;
}

const CollectionBar = ({ links, groupName }: ICollectionBar) => {
  const [opened, setOpened] = useState(false);
  const [isOpen, { open, close }] = useDisclosure(false);
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const meta = useSelector(state => state.generatorSlice.meta);
  const isOnline = useIsOnline();
  const renderLinks = () => {
    if (!links) return null;
    if (links.length < 4) {
      const linksToRender = links.map(link => (
        <Anchor
          key={link.link}
          size={"xs"}
          href={
            link.link.indexOf("http") === -1
              ? getOrigin() + `/${link.link}`
              : `${link.link}`
          }
          target="blank">
          {link.linkName}
        </Anchor>
      ));
      return linksToRender;
    } else {
      return (
        <Menu.Target>
          <Button
            leftIcon={
              <IconChevronDown
                style={{
                  rotate: opened ? "180deg" : "0deg",
                  transition: "0.15s",
                }}
              />
            }
            variant="outline">
            Links
          </Button>
        </Menu.Target>
      );
    }
  };

  const renderMenuItems = () => {
    if (links) {
      const dropdownLinks = links.map(link => {
        return (
          <Menu.Item key={link.link}>
            <Anchor
              underline={false}
              size={"xs"}
              href={getOrigin() + `/${link.link}`}
              target="blank">
              {link.linkName}
            </Anchor>
          </Menu.Item>
        );
      });
      return dropdownLinks;
    }
  };

  return (
    <>
      {meta.currentMeta && <SetLang opened={isOpen} close={close} />}
      <Menu opened={opened} onChange={setOpened}>
        <Menu.Dropdown>{renderMenuItems()}</Menu.Dropdown>
        <Grid m={0} mih={"58px"} align="center" mb={15}>
          <Grid.Col className="flex-row" span="auto">
            <BackButton />
            <Text ml={15} data-cy="collection-name">
              {groupName}
            </Text>
          </Grid.Col>

          <Grid.Col
            p={0}
            className="flex-row"
            span="auto"
            style={{
              display: "flex",
              justifyContent: "center",
            }}>
            <Flex direction={"column"} align={"center"}>
              {links?.length < 4 ? renderLinks() : null}
            </Flex>
          </Grid.Col>

          <Grid.Col
            className="flex-row"
            span="auto"
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px",
            }}>
            {links?.length < 4 ? null : renderLinks()}
            {meta.currentMeta && (
              <>
                <Text size={"sm"} w={300}>
                  Established working language onboard (SOLAS Ch.V Reg 14.3):
                </Text>
                <Text size={"sm"} fw={600} mr={10}>
                  {meta?.currentMetaValue}
                </Text>
                <Button variant="outline" onClick={open}>
                  Set
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => navigate("/summary")}>
              Summary
            </Button>
            <Button variant="outline" onClick={() => navigate("/reports")}>
              Reports
            </Button>
            <Button
              variant="outline"
              leftIcon={<IconSearch size="1rem" />}
              onClick={() => navigate(`/search/${collectionId}?type=colid`)}
              disabled={!isOnline}>
              Search
            </Button>
          </Grid.Col>
        </Grid>
        <Divider my="sm" />
      </Menu>
    </>
  );
};

export default CollectionBar;
